import api, { API_BASE_URL, refreshAccessToken, tokenStore, unwrap } from "./platformClient";
import { getCache, setCache, clearCache } from "../utils/cache";
import { flattenUser } from "../utils/userAdapter";
import { dataUrlToBlob } from "../utils/image";
import outbox from "./attendanceOutbox";

/**
 * SERVICE LAYER — semua panggilan ke BACKEND PLATFORM.
 * Nama-nama export (authAPI, attendanceAPI, leaveAPI, ...) sengaja dipertahankan
 * agar halaman yang sudah ada tidak perlu diubah total. Bentuk data di sebagian
 * fungsi di-"adapt" ke bentuk yang dulu dipakai UI.
 */

export { API_BASE_URL };

/**
 * Tambahkan access token ke URL file (?token=) agar bisa dipakai di <img src>.
 * Tag <img> tidak bisa mengirim header Authorization; backend (authAny) menerima
 * token via query khusus untuk GET /api/files/*.
 */
export function withFileToken(url) {
  if (!url) return null;
  const t = tokenStore.getAccess();
  return t ? `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(t)}` : url;
}

/* ─── AUTH ─────────────────────────────────────────────── */
export const authAPI = {
  // Login pakai username+password platform. Untuk karyawan, "NIK" dikirim
  // sebagai username dan "PIN" sebagai password (akun dibuat saat sync Odoo).
  login: (username, password) =>
    unwrap(api.post("/auth/login", { username, password, device: "hris-web" })),
  me: () => unwrap(api.get("/auth/me")),
  refresh: () => refreshAccessToken(),
  // Tidak menelan error: pemanggil perlu tahu bila logout belum sampai ke server.
  logout: () => api.post("/auth/logout", {}, { timeout: 10000 }),
};

export const healthAPI = {
  // Timeout 10 dtk (bukan 6): di sinyal seluler yang lemah, 6 dtk terlalu sering
  // dianggap "server mati" padahal server sehat.
  check: () => api.get("/health", { timeout: 10000 }),
};

/**
 * Laporan kegagalan koneksi (lihat backend routes/telemetry.routes.js). Dipakai
 * untuk membuktikan PENYEBAB "server belum merespons" — kegagalan yang tak sampai
 * ke server tak terlihat di log mana pun. Gagal mengirim laporan diabaikan.
 */
export const telemetryAPI = {
  connection: (events) => api.post("/telemetry/connection", { events }, { timeout: 8000 }).catch(() => {}),
};

/* ─── FEEDBACK / CUSTOMER SUPPORT ───────────────────────── */
/**
 * Kirim kritik/saran/laporan bug dari halaman Profil. Lampiran (screenshot)
 * OPSIONAL: bila ada, di-upload dulu (pola 2-langkah) → dapat key → dikirim
 * bersama pesan. `file` = objek File dari <input type="file">.
 */
export const feedbackAPI = {
  submit: async ({ category, message, file }) => {
    let attachment_key;
    if (file) {
      const form = new FormData();
      form.append("file", file, file.name || "screenshot");
      const up = await unwrap(
        api.post("/uploads/feedback", form, { headers: { "Content-Type": undefined } })
      );
      attachment_key = up.key;
    }
    return unwrap(api.post("/feedback", { category, message, attachment_key }));
  },
};

/* ─── PROFILE ───────────────────────────────────────────── */
export const profileAPI = {
  // Kembalikan objek user "flat" (lihat userAdapter) supaya kompatibel UI lama.
  get: async (params = {}) => {
    if (!params.force) {
      const cache = getCache("profile");
      if (cache) return cache;
    }
    let data;
    try {
      data = await unwrap(api.get("/profile")); // { user, employee }
    } catch (e) {
      // Server tak terjangkau: pakai profil tersimpan (lokasi kerja, tipe absen)
      // supaya halaman absen tetap bisa dipakai offline.
      const saved = !e?.response || e.response.status >= 500 ? tokenStore.getUser() : null;
      if (saved) return saved;
      throw e;
    }
    const flat = flattenUser(data);
    setCache("profile", flat, 60000);
    return flat;
  },
  updateBankDetails: (data) => unwrap(api.post("/profile/bank", data)),

  /**
   * Ganti foto profil (pola 2-langkah): upload file dulu → dapat key → simpan key.
   * Menerima base64 (hasil kompresi) ATAU Blob/File. Mengembalikan employee
   * terbaru (termasuk photo_url). Cache profil dibersihkan agar UI refresh.
   */
  updatePhoto: async (imageData) => {
    const form = new FormData();
    const blob = imageData instanceof Blob ? imageData : dataUrlToBlob(imageData);
    form.append("file", blob, "profile.jpg");
    const up = await unwrap(
      api.post("/uploads/profile", form, { headers: { "Content-Type": undefined } })
    );
    const employee = await unwrap(api.post("/profile/photo", { photo_key: up.key }));
    clearCache("profile");
    return employee; // { ...employee, photo_url }
  },
};

/* ─── ATTENDANCE ────────────────────────────────────────── */

/**
 * Petakan flag status platform (NORMAL, LATE, HALF_DAY, FORGOT_CHECKIN,
 * FORGOT_CHECKOUT, WFH, dst) ke `status_code` yang dipakai util kalender
 * history (utils/attendanceHistory).
 */
function mapStatusCode(status) {
  switch (status) {
    case "HALF_DAY":
      return "half_day";
    case "FORGOT_CHECKIN":
      return "missing_checkin";
    case "FORGOT_CHECKOUT":
      return "missing_checkout";
    default:
      return null;
  }
}

/**
 * Sesuaikan record absensi platform ke bentuk yang dibaca UI lama:
 *  - worked_minutes → worked_hours
 *  - status → status_code (untuk kalender) + flag is_wfh
 * Catatan: URL foto selfie sengaja null dulu — endpoint file butuh auth header
 * yang tidak bisa dikirim lewat <img src>. Menampilkan selfie kembali =
 * follow-up (perlu file serving yang ramah token).
 */
function adaptAttendance(rec) {
  if (!rec) return rec;
  return {
    ...rec,
    worked_hours: rec.worked_minutes != null ? rec.worked_minutes / 60 : null,
    status_code: mapStatusCode(rec.status),
    is_wfh: rec.work_mode === "wfh" || rec.status === "WFH",
    // URL foto selfie (absolut, dari backend buildUrl). Ditampilkan lewat <img>
    // dengan token query (withFileToken). null bila record tak punya foto.
    checkin_image_url: rec.checkin_photo_url || null,
    checkout_image_url: rec.checkout_photo_url || null,
  };
}

/**
 * Upload selfie absensi → dapat storage key. Ini "langkah 1" dari pola
 * 2-langkah. Foto disimpan sebagai file di server (DB hanya simpan key).
 * Dari sisi user tetap satu aksi; upload ini terjadi di balik layar.
 */
async function uploadAttendancePhoto(imageDataUrl) {
  return outbox.uploadSelfie(imageDataUrl);
}

/**
 * Kirim absen lewat ANTREAN (services/attendanceOutbox): disimpan di HP dulu, baru
 * dikirim. Sukses → record server. Gagal karena jaringan/server sibuk → TIDAK
 * error: absen tetap tersimpan & dikirim otomatis nanti, dan fungsi ini
 * mengembalikan { queued: true, ... } supaya UI bisa memberi tahu user.
 * Ditolak aturan bisnis (mis. di luar radius) → melempar error seperti biasa.
 * Bila IndexedDB tak tersedia (mode Private) → alur lama: langsung kirim.
 */
async function submitAttendance(type, payload) {
  const { image, ...fields } = payload;

  if (await outbox.isSupported()) {
    const res = await outbox.submit(type, { image, fields });
    if (res.status === "queued") return outbox.queuedView(res.item);
    clearCache("attendance_today");
    clearCache("attendance_history");
    return adaptAttendance(res.record);
  }

  let photo_key;
  if (image) photo_key = await uploadAttendancePhoto(image);
  const path = type === "in" ? "/attendance/checkin" : "/attendance/checkout";
  const record = await unwrap(api.post(path, { ...fields, photo_key, client_request_id: outbox.uuid() }));
  clearCache("attendance_today");
  clearCache("attendance_history");
  return adaptAttendance(record);
}

export const attendanceAPI = {
  today: async () => {
    const cache = getCache("attendance_today");
    let server = cache;
    if (!server) {
      try {
        server = adaptAttendance(await unwrap(api.get("/attendance/today")));
      } catch (e) {
        // Server tak terjangkau: kalau ada absen yang masih antre, tetap tampilkan
        // itu (bukan "belum absen"/error kosong).
        const pending = await outbox.mergePendingIntoToday(null);
        if (pending) return pending;
        throw e;
      }
      setCache("attendance_today", server, 30000);
    }
    // Cache hanya menyimpan data SERVER; absen yang masih antre ditempel tiap dibaca.
    return outbox.mergePendingIntoToday(server);
  },

  // UI history membaca `data.records` → bungkus array platform jadi { records }.
  history: async (params) => {
    const key = `attendance_history_${params.date_from}_${params.date_to}`;
    const cache = getCache(key);
    if (cache) return cache;
    const rows = (await unwrap(api.get("/attendance/history", { params }))) || [];
    const data = { records: rows.map(adaptAttendance) };
    setCache(key, data, 30000);
    return data;
  },

  // payload: { image(base64), latitude, longitude, work_mode, address, attendance_time_* ... }
  checkIn: (payload) => submitAttendance("in", payload),
  checkOut: (payload) => submitAttendance("out", payload),
};

/* ─── LEAVE ─────────────────────────────────────────────── */
export const leaveAPI = {
  types: async () => {
    const cache = getCache("leave_types");
    if (cache) return cache;
    const data = await unwrap(api.get("/leave/types"));
    setCache("leave_types", data, 60000);
    return data;
  },

  // Adapt bentuk saldo ke yang dipakai Dashboard: tambahkan total_remaining
  // dan alias used_days per baris.
  balance: async () => {
    const cache = getCache("leave_balance");
    if (cache) return cache;
    const raw = await unwrap(api.get("/leave/balance")); // { year, source, synced, balances:[{quota,used,remaining}] }
    const balances = (raw?.balances || []).map((b) => ({
      ...b,
      used_days: b.used,
      remaining_days: b.remaining,
    }));
    const adapted = {
      ...raw,
      balances,
      total_quota: balances.reduce((a, b) => a + (b.quota || 0), 0),
      total_used: balances.reduce((a, b) => a + (b.used || 0), 0),
      total_remaining: balances.reduce((a, b) => a + (b.remaining || 0), 0),
    };
    setCache("leave_balance", adapted, 30000);
    return adapted;
  },

  history: async (params = {}) => {
    const key = `leave_history_${tokenStore.getUser()?.id || "default"}`;
    if (!params.force) {
      const cache = getCache(key);
      if (cache) return cache;
    }
    const data = await unwrap(api.get("/leave", { params }));
    setCache(key, data, 30000);
    return data;
  },

  apply: async (data) => {
    const res = await unwrap(api.post("/leave", data));
    clearCache("leave_balance");
    clearCache("leave_history");
    return res;
  },

  cancel: async (id) => {
    const res = await unwrap(api.post(`/leave/${id}/cancel`));
    clearCache("leave_history");
    return res;
  },
};

/* ─── DASHBOARD ─────────────────────────────────────────── */
export const dashboardAPI = {
  summary: async () => {
    const cache = getCache("dashboard_summary");
    if (cache) return cache;
    const data = await unwrap(api.get("/dashboard/summary"));
    setCache("dashboard_summary", data, 20000);
    return data;
  },
};

/* ─── NOTIFICATION ──────────────────────────────────────── */
export const notificationAPI = {
  list: (params = {}) => unwrap(api.get("/notification", { params })),
  unreadCount: () => unwrap(api.get("/notification/unread-count")),
  markRead: (id) => unwrap(api.post(`/notification/${id}/read`)),
  markAllRead: () => unwrap(api.post("/notification/read-all")),
};

/* ─── PAYSLIP (belum ada di platform — stub) ────────────── */
export const payslipAPI = {
  list: async () => [],
  detail: async () => null,
};

export default api;
